import { type Components, type Theme, tooltipClasses } from '@mui/material'
import type { PickerComponents } from '@mui/x-date-pickers-pro/themeAugmentation'
import { reminder } from './colors'
import type { CustomThemeOptions } from './types'

interface CustomComponents extends Components<Omit<Theme, 'components'>> {
  MuiDatePicker?: PickerComponents<Theme>['MuiDatePicker']
  MuiPickersOutlinedInput?: PickerComponents<Theme>['MuiPickersOutlinedInput']
}

const defaultComponents = (customOptions?: CustomThemeOptions['components']): CustomComponents => ({
  MuiPickersOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: Number(theme.shape.borderRadius) * 2,
        backgroundColor: theme.palette.background.paper,
        '&.Mui-focused': {
          backgroundColor: theme.palette.background.paper,
        },
        '&.Mui-disabled': {
          backgroundColor: theme.palette.background.paper,
        },
      }),
      sectionsContainer: ({ theme }) => ({
        padding: theme.spacing(1.5625, 0), // 12.5px/8px
        flexWrap: 'wrap',
        overflow: 'visible',
      }),
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: () => ({
        borderRadius: 8,
      }),
      action: {
        minWidth: 'fit-content',
      },
    },
  },
  MuiList: {
    styleOverrides: {
      root: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderColor: theme.palette.grey['300'],
      }),
    },
  },
  MuiButton: {
    styleOverrides: {
      sizeLarge: ({ theme }) => ({
        height: 48,
        ...theme.typography.p2Medium,
        padding: theme.spacing(1, 1.5),
      }),
      sizeMedium: ({ theme }) => ({
        ...theme.typography.p2Medium,
        padding: theme.spacing(1, 1.5),
      }),
      sizeSmall: ({ theme }) => ({
        ...theme.typography.p3Medium,
        padding: theme.spacing(0.5, 1.5),
      }),
      root: ({ theme }) => ({
        borderRadius: customOptions?.buttonBorderRadius ?? Number(theme.shape.borderRadius) * 2,
        variants: [
          {
            props: {
              variant: 'outlined',
              color: 'primary',
            },
            style: {
              color: theme.palette.text.primary,
              borderColor: theme.palette.text.primary,
              '&:hover': {
                backgroundColor: theme.palette.grey['200'],
              },
              '&:active': {
                backgroundColor: theme.palette.grey['200'],
              },
              '&:disabled': {
                color: theme.palette.text.secondary,
                backgroundColor: theme.palette.grey['300'],
              },
            },
          },
        ],
      }),
    },
    variants: [
      {
        props: { variant: 'link' },
        style: ({ theme }) => ({
          minWidth: 'fit-content',
          color: theme.palette.primary.main,
          padding: 0,
          height: 'fit-content',
          '&:hover': {
            backgroundColor: 'transparent',
          },
          '&:focus-visible': {
            borderRadius: theme.shape.borderRadius,
          },
        }),
      },
    ],
    defaultProps: {
      size: 'large',
    },
  },
  MuiButtonBase: {
    styleOverrides: {
      root: ({ theme }) => ({
        // Calendar
        '&.MuiPickersDay-root': {
          fontSize: '14px',
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 4,
        },
      }),
    },
    defaultProps: { disableRipple: true },
  },
  MuiLink: {
    defaultProps: {
      underline: 'none',
    },
  },
  MuiTypography: {
    styleOverrides: {
      root: ({ theme }) => ({
        // Calendar
        '&.MuiPickersToolbarText-root': {
          fontSize: '14px',
          color: theme.palette.text.secondary,
        },
      }),
    },
    defaultProps: {
      variantMapping: {
        title: 'h1',
        p1SemiBold: 'p',
        p1Medium: 'p',
        p1Regular: 'p',
        p2SemiBold: 'p',
        p2Medium: 'p',
        p2Regular: 'p',
        p3SemiBold: 'p',
        p3Medium: 'p',
        p3Regular: 'p',
        p4SemiBold: 'p',
        p4Medium: 'p',
        p4Regular: 'p',
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: Number(theme.shape.borderRadius) * 2,
        borderColor: theme.palette.grey['300'],
        '&:hover:not($disabled):not($focused):not($error) $notchedOutline': {
          borderColor: theme.palette.grey['300'],
        },
      }),
      input: ({ theme }) => ({
        borderRadius: Number(theme.shape.borderRadius) * 2,
        paddingVertical: theme.spacing(1.5),
      }),
      multiline: {
        borderRadius: 'initial',
      },
      notchedOutline: ({ theme }) => ({
        borderColor: theme.palette.grey['300'],
      }),
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.p2Regular,
        color: theme.palette.text.secondary,
      }),
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: ({ theme }) => ({
        height: 48,
        backgroundColor: theme.palette.background.paper,
        ...theme.typography.p2Regular,
      }),
      multiline: ({ theme }) => ({
        '&.MuiInputBase-root': {
          height: 'auto',
          padding: theme.spacing(1.5, 1.75),
        },
      }),
      sizeSmall: () => ({
        height: 38,
      }),
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      outlined: {
        top: '-3px',
        '&$focused': {
          top: 0,
        },
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        margin: '3px 0 0 3px',
      },
    },
  },
  MuiTooltip: {
    defaultProps: {
      slotProps: {
        // Only apply to placement bottom for Figma now
        popper: {
          sx: {
            [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]: {
              marginTop: 0,
            },
          },
        },
      },
    },
    styleOverrides: {
      tooltip: ({ theme }) => ({
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.background.paper,
        borderRadius: Number(theme.shape.borderRadius) * 2,
        boxShadow: theme.shadows[3],
        padding: theme.spacing(1),
        fontFamily: theme.typography.p4Medium.fontFamily,
      }),
      popper: {
        marginTop: '4px',
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: ({ theme }) => ({
        height: '6px',
        borderRadius: Number(theme.shape.borderRadius) * 2,
        backgroundColor: theme.palette.grey['200'],
        width: '100%',
      }),
      bar: ({ theme }) => ({
        height: '6px',
        borderRadius: Number(theme.shape.borderRadius) * 2,
        backgroundColor: theme.palette.primary.main,
        width: '100%',
      }),
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: ({ theme }) => ({
        minHeight: 48,
        [theme.breakpoints.down('md')]: {
          minHeight: 32,
        },
      }),
      scrollButtons: {
        opacity: 1,
      },
      indicator: ({ theme }) => ({
        backgroundColor: theme.palette.common.black,
        height: 3,
      }),
      scroller: ({ theme }) => ({
        borderBottom: `1px solid ${theme.palette.grey['400']}`,
      }),
      list: ({ theme }) => ({
        gap: theme.spacing(2),
      }),
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontSize: theme.typography.p2Medium.fontSize,
        [theme.breakpoints.down('sm')]: {
          fontSize: theme.typography.p2Medium.fontSize,
        },
        [theme.breakpoints.up('lg')]: {
          fontSize: theme.typography.p2Medium.fontSize,
        },
        color: theme.palette.text.secondary,
        textTransform: 'none',
        '&:hover': {
          color: theme.palette.text.primary,
        },
        '&.Mui-selected': {
          color: theme.palette.text.primary,

          '> .MuiTab-iconWrapper > span': {
            backgroundColor: theme.palette.common.black,
          },
        },
        minHeight: 48,
        minWidth: 0,
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: 16,
        [theme.breakpoints.down('md')]: {
          minHeight: 32,
        },
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
      }),
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&.MuiMenu-paper': {
          '&.MuiPopover-paper': {
            '&.MuiPaper-elevation8': {
              boxShadow: theme.shadows[5],
            },
          },
        },
      }),
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:hover': {
          backgroundColor: theme.palette.primary.lightest,
        },
      }),
    },
  },
  MuiChip: {
    variants: [
      {
        props: { variant: 'new' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.grey['200'],
          color: theme.palette.getContrastText(theme.palette.grey['200']),
        }),
      },
      {
        props: { variant: 'submitted' },
        style: ({ theme }) => ({
          backgroundColor: reminder['100'],
          color: theme.palette.error.darker,
        }),
      },
      {
        props: { variant: 'rejected' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.warning.light,
          color: theme.palette.getContrastText(theme.palette.warning.light),
        }),
      },
      {
        props: { variant: 'approved' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.success.lightest,
          color: theme.palette.getContrastText(theme.palette.success.lightest),
        }),
      },
      {
        props: { variant: 'exported' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        }),
      },
      {
        props: { variant: 'exporting' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.info.lightest,
          color: theme.palette.info.darker,
        }),
      },
      {
        props: { variant: 'exportFailed' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.error.main,
          color: theme.palette.error.contrastText,
        }),
      },
      {
        props: { variant: 'paidOut' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.success.main,
          color: theme.palette.success.contrastText,
        }),
      },
      {
        props: { variant: 'notPaidOut' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.grey['200'],
          color: theme.palette.getContrastText(theme.palette.grey['200']),
        }),
      },
      {
        props: { variant: 'personChip' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.background.default,
        }),
      },
    ],
    styleOverrides: {
      root: {
        borderRadius: 4,
        height: '22px',
      },
      label: ({ theme }) => ({
        paddingTop: 2,
        paddingBottom: 2,
        paddingLeft: 4,
        paddingRight: 4,
        ...theme.typography.p4Medium,
        lineHeight: '100%',
        color: 'inherit',
      }),
      avatar: ({ theme }) => ({
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.main,
      }),
    },
  },
  MuiSwitch: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        width: 40,
        height: 24,
        padding: 0,
      },
      switchBase: ({ theme }) => ({
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&.Mui-checked': {
          transform: 'translateX(16px)',
          color: theme.palette.common.white,
          opacity: 1,
          '& + .MuiSwitch-track': {
            backgroundColor: theme.palette.primary.main,
            opacity: 1,
            border: 0,
          },
          '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.5,
          },
          '&.Mui-disabled .MuiSwitch-thumb': {
            color: theme.palette.grey['300'],
          },
        },
        '&.Mui-focusVisible .MuiSwitch-thumb': {
          color: theme.palette.primary.main,
          border: `5px solid ${theme.palette.common.white}`,
        },
        '&.Mui-disabled .MuiSwitch-thumb': {
          color: theme.palette.grey['400'],
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.7,
        },
      }),
      thumb: {
        width: 20,
        height: 20,
        boxShadow: '0 2px 1px 0 rgba(0, 0, 0, 0.08)',
      },
      track: ({ theme }) => ({
        borderRadius: 24 / 2,
        backgroundColor: theme.palette.grey['300'],
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
          duration: 300,
        }),
      }),
    },
  },
  MuiBadge: {
    styleOverrides: {
      badge: {
        minWidth: '5px',
        height: '5px',
        right: '-2px',
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: Number(theme.shape.borderRadius) * 2,
      }),
      root: {
        padding: '32px',
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '16px',
        paddingTop: '0px',
        marginLeft: 0,
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(4),
        paddingTop: theme.spacing(2),
      }),
    },
  },
  MuiDialogContentText: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(4),
        paddingTop: theme.spacing(2),
      }),
    },
    defaultProps: {
      variant: 'p2Regular',
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(4),
        paddingBottom: 0,
      }),
    },
    defaultProps: {
      variant: 'h2',
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderColor: theme.palette.grey['300'],
      }),
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      paper: ({ theme }) => ({
        boxShadow: theme.shadows[3],
        borderRadius: Number(theme.shape.borderRadius) * 2,
      }),
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: Number(theme.shape.borderRadius) * 2,
      }),
    },
  },
  MuiCard: {
    defaultProps: {
      variant: 'outlined',
    },
    variants: [
      {
        props: { variant: 'elevation' },
        style: () => ({
          boxShadow: 'none',
        }),
      },
      {
        props: { variant: 'outlined' },
        style: ({ theme }) => ({
          '&.MuiPaper-root': {
            border: `1px solid ${theme.palette.grey['200']}`,
            transition: 'box-shadow 300ms ease',

            '&:hover': {
              boxShadow: theme.shadows[5],
            },
          },
        }),
      },
    ],
    styleOverrides: {
      root: ({ theme }) => ({
        '&.MuiPaper-root': {
          backgroundColor: theme.palette.background.paper,
          borderRadius: Number(theme.shape.borderRadius) * 2,
        },
      }),
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: () => ({
        ':last-child': {
          paddingBottom: 16,
        },
      }),
    },
  },
  MuiCardActionArea: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:focus-visible': { borderRadius: theme.shape.borderRadius },
      }),
    },
  },
  MuiBackdrop: {
    styleOverrides: {
      root: {
        backgroundColor: 'rgba(0, 0, 0, 0.35)',
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '>.MuiFormHelperText-root': {
          lineHeight: '1.2',
        },
      },
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: Number(theme.shape.borderRadius) * 2,
        backgroundColor: theme.palette.grey['200'],
        padding: theme.spacing(0.5),
        gap: theme.spacing(0.5),
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        height: 38,
        '&.MuiToggleButtonGroup-middleButton, &.MuiToggleButtonGroup-firstButton, &.MuiToggleButtonGroup-lastButton': {
          borderRadius: Number(theme.shape.borderRadius) * 2,
          marginLeft: 0,
          border: 0,
        },
        '&.Mui-selected, &.Mui-selected:hover': {
          backgroundColor: theme.palette.background.paper,
        },
      }),
    },
  },
})

export { defaultComponents }
